import React, { useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { Box, Button } from '@mui/material';
import { useAuth } from '@/core/contexts/AuthContext';
import { WebSocketProvider } from '@/core/contexts/WebSocketContext';
import { RegistrationProvider } from '@/core/contexts/RegistrationContext';
import Login from '@/features/auth/Login';
import ForgotPassword from '@/features/auth/components/ForgotPassword';
import ResetPassword from '@/features/auth/components/ResetPassword';
import RegistrationContainer from '@/features/auth/registration/RegistrationContainer';
import DashboardContainer from '@/features/dashboard/DashboardContainer';
import RevShareSigningPage from '@/features/sign/RevShareSigningPage';
import SubscriptionSigningPage from '@/features/sign/SubscriptionSigningPage';
import AcceptInvitationPage from '@/features/auth/AcceptInvitationPage';
import MarketingClickRedirect from '@/features/auth/marketing/MarketingClickRedirect';
import MarketingPreferencesPage from '@/features/auth/marketing/MarketingPreferencesPage';
import LoadingScreen from '@/components/common/LoadingScreen';
import usePreferenceThemeSync from '@/hooks/usePreferenceThemeSync';
import useRealTimeNotifications from '@/hooks/useRealTimeNotifications';
import useRealTimeCrmUpdates from '@/hooks/useRealTimeCrmUpdates';
import ChipNotificationManager from '@/features/dashboard/components/ChipNotificationManager';
import AppInstallBanner from '@/components/layout/AppInstallBanner';
import AppUpdateBanner from '@/components/layout/AppUpdateBanner';
import ServiceWorkerUpdateCoordinator from '@/components/layout/ServiceWorkerUpdateCoordinator';
import HelpRoutes from '@/features/help/HelpRoutes';

const App = () => {
  const { isAuthenticated, loading, finalizeRegistrationSession, error } = useAuth();
  const navigate = useNavigate();
  
  usePreferenceThemeSync();
  useRealTimeNotifications();
  useRealTimeCrmUpdates();

  const handleRegistrationComplete = useCallback(async (registrationResult) => {
    try {
      const hasTokens =
        registrationResult?.accessToken ||
        registrationResult?.access_token ||
        registrationResult?.token ||
        registrationResult?.idToken;
      if (hasTokens) {
        const userData = await finalizeRegistrationSession(registrationResult);
        const role =
          userData?.role ||
          registrationResult?.user?.role ||
          registrationResult?.role;
        if (role === 'investor') {
          navigate('/dashboard/onboarding', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } else {
        navigate('/login', { replace: true });
      }
    } catch {
      navigate('/login', { replace: true });
    }
  }, [finalizeRegistrationSession, navigate]);
  
  // Don't render routes until we know the authentication state
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (error) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: 2,
      }}>
        <Box component="h2" sx={{ m: 0, color: 'text.primary' }}>Authentication Error</Box>
        <Box component="p" sx={{ m: 0, color: 'text.secondary' }}>{error}</Box>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Reload Page
        </Button>
      </Box>
    );
  }
  
  return (
    <GoogleReCaptchaProvider reCaptchaKey="6Ldom-srAAAAAGeD_3HhILqcy9ytuRKYWdCr33P0">
      <WebSocketProvider>
        <AppUpdateBanner />
        <ServiceWorkerUpdateCoordinator />
        <AppInstallBanner />
        <ChipNotificationManager />
        <Routes>
        <Route
          path="/"
          element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />}
        />
        <Route 
          path="/login" 
          element={!isAuthenticated ? <Login /> : <Navigate to="/dashboard" replace />} 
        />
          
          <Route 
            path="/forgot-password" 
            element={!isAuthenticated ? <ForgotPassword /> : <Navigate to="/dashboard" replace />} 
          />
          
          <Route 
            path="/reset-password" 
            element={!isAuthenticated ? <ResetPassword /> : <Navigate to="/dashboard" replace />} 
          />
          
          <Route 
            path="/register/*" 
            element={
              !isAuthenticated ? (
                <RegistrationProvider>
                  <RegistrationContainer onRegistrationComplete={handleRegistrationComplete} />
                </RegistrationProvider>
              ) : (
                <Navigate to="/dashboard" replace />
              )
            } 
          />
          
          {/* Public routes — HFR approver countersignature pages (no auth required) */}
          <Route path="/admin/settings/agreements/sign/rev-share" element={<RevShareSigningPage />} />
          <Route path="/admin/settings/agreements/sign/subscription" element={<SubscriptionSigningPage />} />

          {/* Manager one-time invite redemption (no auth required) */}
          <Route path="/accept-invitation" element={<AcceptInvitationPage />} />

          <Route path="/marketing/preferences" element={<MarketingPreferencesPage />} />
          <Route path="/marketing/click" element={<MarketingClickRedirect />} />

          <Route path="/help/*" element={<HelpRoutes />} />

          <Route path="/investor" element={<Navigate to="/dashboard/investor" replace />} />
          <Route path="/manager" element={<Navigate to="/dashboard/manager" replace />} />
          <Route path="/admin" element={<Navigate to="/dashboard/admin" replace />} />

          <Route
            path="/dashboard/*"
            element={isAuthenticated ? <DashboardContainer /> : <Navigate to="/login" replace />}
          />

          <Route
            path="*"
            element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />}
          />
        </Routes>
      </WebSocketProvider>
    </GoogleReCaptchaProvider>
  );
};

export default App;
